import { useT } from "../../../shared/i18n/i18n";
import { useState } from "react";
import type { WorkObjectTypeDefinition } from "../../../entities/work-object-type/model/use-work-object-types";
import { useWorkObjectTypes } from "../../../entities/work-object-type/model/use-work-object-types";
import { WorkObjectTypeList } from "../../../entities/work-object-type/ui/work-object-type-list";
import { WorkObjectTypeDialog } from "../../../features/work-object-types/manage-type/ui/work-object-type-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

type WorkObjectTypesPanelProps = {
  onChanged?: () => void | Promise<void>;
};

export function WorkObjectTypesPanel({ onChanged }: WorkObjectTypesPanelProps) {
  const t = useT();
  const types = useWorkObjectTypes();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<WorkObjectTypeDefinition | null>(null);

  function openType(type: WorkObjectTypeDefinition | null) {
    setSelectedType(type);
    setIsDialogOpen(true);
  }

  async function notifyChanged() {
    await onChanged?.();
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t("itemTypes.title")}</CardTitle>
          <CardDescription>{t("itemTypes.whatItIsPanel")}</CardDescription>
        </div>
        <CardAction>
          <Button onClick={() => openType(null)} size="sm">
            {t("itemTypes.newType")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {types.isLoading ? <Skeleton className="h-24 w-full" /> : null}
        {types.error ? (
          <Alert variant="destructive">
            <AlertDescription>{types.error}</AlertDescription>
          </Alert>
        ) : null}
        {!types.isLoading && !types.error ? <WorkObjectTypeList onSelect={openType} types={types.data} /> : null}
        <WorkObjectTypeDialog
          categories={types.categories}
          existingTypes={types.data}
          isArchiving={types.isArchiving}
          isSaving={types.isCreating || types.isUpdating}
          onArchive={async (typeId) => {
            await types.archiveType(typeId);
            setIsDialogOpen(false);
            setSelectedType(null);
            await notifyChanged();
          }}
          onArchiveCategory={types.archiveCategory}
          onCreateCategory={types.createCategory}
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setSelectedType(null);
            }
          }}
          onRenameCategory={types.updateCategory}
          onSave={async (input, type) => {
            if (type) {
              await types.updateType({ ...input, id: type.id });
            } else {
              await types.createType(input);
            }
            setIsDialogOpen(false);
            setSelectedType(null);
            await notifyChanged();
          }}
          open={isDialogOpen}
          type={selectedType}
        />
      </CardContent>
    </Card>
  );
}
